import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { prints, numberWord } from '../content';
import { FadeUp, PageHeader } from '../components/Reveal';
import Lightbox from '../components/Lightbox';

const Shop = () => {
    const { lang, t } = useLanguage();
    const navigate = useNavigate();
    const [openIndex, setOpenIndex] = useState(null);

    return (
        <main data-testid="shop-page">
            <PageHeader kicker={`Robin Spencer — ${t.nav.shop}`} title={t.shop.title} sub={t.shop.sub} />

            <section className="px-6 md:px-12 pb-12 max-w-[1400px] mx-auto">
                <FadeUp>
                    <p className="border-l-2 border-dusk pl-6 text-sm md:text-base text-fog max-w-2xl leading-relaxed" data-testid="shop-note">
                        {t.shop.note}
                    </p>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-6 text-xs tracking-[0.25em] uppercase text-fog" data-testid="shop-count">
                        {prints.length} {t.shop.available}
                    </p>
                </FadeUp>
            </section>

            <section className="px-6 md:px-12 py-10 pb-32 md:pb-44 max-w-[1400px] mx-auto">
                <div
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-5 gap-y-10"
                    data-testid="prints-grid"
                >
                    {prints.map((p, i) => (
                        <FadeUp key={p.slug} delay={(i % 6) * 0.04}>
                            <article className="group" data-testid={`print-card-${p.slug}`}>
                                <button
                                    type="button"
                                    onClick={() => setOpenIndex(i)}
                                    className="block w-full text-left"
                                    aria-label={numberWord(i + 1, lang)}
                                    data-testid={`print-open-${p.slug}`}
                                >
                                    <div className="spotlight">
                                        <div className="overflow-hidden">
                                            <img
                                                src={`/images/${p.art}.jpg`}
                                                alt={numberWord(i + 1, lang)}
                                                className="w-full aspect-square object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                                                loading="lazy"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-baseline justify-between gap-2">
                                        <h2 className="font-serif italic text-base tracking-tight text-paper lowercase">
                                            {numberWord(i + 1, lang)}
                                        </h2>
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-fog/70">
                                            №&nbsp;{String(i + 1).padStart(2, '0')}
                                        </span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => navigate(`/contact?ref=${p.slug}`)}
                                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-fog transition-colors duration-300 hover:text-dusk"
                                    data-testid={`print-enquire-${p.slug}`}
                                >
                                    {t.shop.enquire}
                                    <ArrowUpRight className="w-3 h-3" />
                                </button>
                            </article>
                        </FadeUp>
                    ))}
                </div>
            </section>

            <Lightbox
                item={openIndex !== null ? prints[openIndex].art : null}
                title={openIndex !== null ? numberWord(openIndex + 1, lang) : ''}
                onClose={() => setOpenIndex(null)}
                onPrev={() => setOpenIndex((i) => (i - 1 + prints.length) % prints.length)}
                onNext={() => setOpenIndex((i) => (i + 1) % prints.length)}
                cta={openIndex !== null ? { label: t.shop.enquire, onClick: () => navigate(`/contact?ref=${prints[openIndex].slug}`) } : null}
            />
        </main>
    );
};

export default Shop;
